import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { FormControl } from "baseui/form-control";
import { Input } from "baseui/input";
import { Checkbox } from "baseui/checkbox";
import { RadioGroup, Radio, ALIGN } from "baseui/radio";
import { Button } from "baseui/button";
import { StatefulDatepicker } from "baseui/datepicker";
import { Notification } from "baseui/notification";
import request from "../../utils/http";
import toaster from "../../utils/toaster";
import TwoColumnGrid from "../../components/grid/TwoColumnGrid";
import { useNavigate } from "react-router-dom";
import AppContext from "../../AppContext";
import { Select } from "baseui/select";
import enums from "../../utils/enums";
import { dateFromDateArray, zonelessIsoDate } from "../../utils/date";
import { CandidateSite } from "../../models/Candidate";
import { TimePicker } from "baseui/timepicker";

const Container = styled.div`
	label.required::after {
		content: " *";
		color: red;
`;

const SiteDaysContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
	grid-column-gap: 1rem;
`;

const GridSpacer = styled.div`
	margin-top: 1rem;
	display: grid;
	grid-row-gap: 1rem;
`;

const StyledSelect = styled(Select)`
	width: 40%;
`;

const CandidateCreate = () => {
	const navigate = useNavigate();
	const appContext = useContext(AppContext);

	const [allOnboardingTaskSets, setAllOnboardingTaskSets] = useState(
		null as any
	);
	const [allEmploymentAgreements, setAllEmploymentAgreements] = useState(
		null as any
	);
	const [allPositionDescriptions, setAllPositionDescriptions] = useState(
		null as any
	);
	const [allDepartments, setAllDepartments] = useState([] as any);
	const [jobCodes, setJobCodes] = useState([] as any);
	const [jobCodeRates, setJobCodeRates] = useState([] as any);
	const [jobCodeDefaultRate, setJobCodeDefaultRate] = useState([] as any);

	useEffect(() => {
		if (candidateSites == null || candidateSites.length == 0) {
			setCandidateSites([
				{
					jobCodeRate: hourlyRate,
					siteId: 1,
					customHourlyRate: customHourlyRate,
				} as CandidateSite,
			]);
		}
		getCandidateTaskSets();
		getCandidateEmploymentAgreements();
		getZambionDepartments();
		getAllPositionDescriptions();
		loadJobCodes({ pageNumber: 1, pageSize: 2000, searchText: "" });
		loadJobCodeRates();
		loadDefaultJobCodeRates();
		getAdminUsers();
	}, []);

	const getCandidateTaskSets = async () => {
		let result = await request(
			"GET",
			"/api/admin/utility/candidate/task-sets",
			null,
			true,
			false
		);
		if (result.success) {
			setAllOnboardingTaskSets(result.data);
			let tmpSelectedTaskSets = [];
			for (let taskSet in result.data.items) {
				if (result.data.items[taskSet].default) {
					tmpSelectedTaskSets.push(result.data.items[taskSet].identifier);
				}
			}
			setSelectedTaskSets(tmpSelectedTaskSets);
		} else {
			toaster.error("Unable to access onboarding tasks");
		}
	};

	const getCandidateEmploymentAgreements = async () => {
		let result = await request(
			"GET",
			"/api/admin/utility/candidate/employment-agreements",
			null,
			true,
			false
		);
		if (result.success) {
			// setAllEmploymentAgreements(result.data);
			let filteredEmploymentAgreements = [] as Array<any>;

			for (let employmentAgreement in result.data.items) {
				if (!result.data.items[employmentAgreement].slug.includes("pil-") && !result.data.items[employmentAgreement].slug.includes("no-choice") && !result.data.items[employmentAgreement].slug.includes("awunz")) {
						filteredEmploymentAgreements.push(
							result.data.items[employmentAgreement]
						);
				}
			}
			setAllEmploymentAgreements(filteredEmploymentAgreements);


			let tmpAvailableEmploymentAgreements = [] as Array<string>;

			for (let employmentAgreement in filteredEmploymentAgreements) {
				if (filteredEmploymentAgreements[employmentAgreement].default) {
						tmpAvailableEmploymentAgreements.push(
							filteredEmploymentAgreements[employmentAgreement].identifier
						);
				}
			}
			setAvailableEmploymentAgreements(tmpAvailableEmploymentAgreements);
			setDefaultEmploymentAgreements(tmpAvailableEmploymentAgreements);

			let tmpHolidayVisaEmploymentAgreements = [] as Array<string>;

			for (let employmentAgreementHV in filteredEmploymentAgreements) {
				if (filteredEmploymentAgreements[employmentAgreementHV].workingHolidayDefault) {
					tmpHolidayVisaEmploymentAgreements.push(
						filteredEmploymentAgreements[employmentAgreementHV].identifier
					);
				}
			}
			setHolidayVisaEmploymentAgreements(tmpHolidayVisaEmploymentAgreements);
		} else {
			toaster.error("Unable to access employment agreements");
		}
	};

	const getZambionDepartments = async () => {
		let result = await request(
			"GET",
			"/api/admin/candidate-zambion/departments",
			null,
			true,
			false
		);
		if (result.success && result.data?.departments) {
			setAllDepartments(result.data.departments);
		} else {
			toaster.error("Unable to access departments");
		}
	};

	const getAllPositionDescriptions = async () => {
		let result = await request(
			"GET",
			"/api/admin/asset-file/all/PositionDescription",
			null,
			true,
			false
		);
		if (result.success) {
			setAllPositionDescriptions(result.data.files);
		} else {
			toaster.error("Unable to access position descriptions");
		}
	};

	interface LoadJobCodesParams {
		pageNumber: number;
		pageSize: number;
		searchText: string;
	}

	const loadJobCodes = async ({
		pageNumber,
		pageSize,
		searchText,
	}: LoadJobCodesParams) => {
		try {
			const params = new URLSearchParams({
				pageNumber: pageNumber.toString(),
				pageSize: pageSize.toString(),
				searchText,
			});

			const url = `/api/admin/jobcode?${params.toString()}`;
			const result = await request("GET", url, null);

			if (result.success && result.data) {
				const activeJobCodes = result.data.jobCodes.filter(
					(code: { active: boolean }) => code.active
				);
				setJobCodes(activeJobCodes);
			}
		} catch (error) {
			// Handle error loading job codes.
		}
	};

	const loadJobCodeRates = async () => {
		try {
			const result = await request("GET", "/api/admin/jobcoderate", null);

			if (result.success && result.data) {
				const activeJobCodeRates = result.data.jobCodeRates.filter(
					(rate: { active: boolean }) => rate.active
				);
				setJobCodeRates(activeJobCodeRates);
			}
		} catch (error) {
			// Handle error loading job codes.
		}
	};

	const loadDefaultJobCodeRates = async () => {
		try {
			const result = await request("GET", "/api/admin/jobcoderate", null);

			if (result.success && result.data) {
				const jobCodeDefault = result.data.jobCodeRates.filter(
					(rate: { active: boolean }) => rate.active,
				).filter(
					(rate: { defaultRate: boolean }) => rate.defaultRate,
				);

				setJobCodeDefaultRate(jobCodeDefault);
				setHourlyRate(jobCodeDefault);
			}
		} catch (error) {
			// Handle error loading job codes.
		}
	};

	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [contactEmail, setContactEmail] = useState("");
	const [contactMobile, setContactMobile] = useState("");

	const [replacingName, setReplacingName] = useState("");
	const [replacingTerminated, setReplacingTerminated] = useState(false);

	const [isWorkingHoliday, setIsWorkingHoliday] = useState(false);
	const [isTOU, setIsTOU] = useState(false);
	const [previouslyWorked, setPreviouslyWorked] = useState(false);
	const [previouslyWorkedComment, setPreviouslyWorkedComment] = useState("");
	const [previouslyWorkedPinNumber, setPreviouslyWorkedPinNumber] = useState(0);
	const [availableFullTime, setAvailableFullTime] = useState(false);
	const [availablePartTime, setAvailablePartTime] = useState(false);
	const [partTimeConfirm, setPartTimeConfirm] = useState(false);

	const [position, setPosition] = useState<any>([]);
	const [positionDescriptionValue, setPositionDescriptionValue] = useState<any>(
		[]
	);

	const [hepBMonitoringRequired, setHepBMonitoringRequired] = useState(false);
	const [drivingCompanyVehicle, setDrivingCompanyVehicle] = useState(false);
	const [kerbsideCollection, setKerbsideCollection] = useState(false);
	const [waterBlasting, setWaterBlasting] = useState(false);
	const [workingAtPublicSchool, setWorkingAtPublicSchool] = useState(false);
	const [workingAtPublicSchoolWeeksValue, setWorkingAtPublicSchoolWeeksValue] =
		useState<any>([]);
	const [hourlyRate, setHourlyRate] = useState<any>([]);
	const [customHourlyRate, setCustomHourlyRate] = useState(false);
	const [customHourlyRateValue, setCustomHourlyRateValue] = useState(0);
	const [customHourlyRateReason, setCustomHourlyRateReason] = useState("");
	const [reasonForAgreement, setReasonForAgreement] = useState("");
	const [approvedByRegionalManager, setApprovedByRegionalManager] =
		useState("");
	const [employmentTypeValue, setEmploymentTypeValue] = useState<any>([]);
	const [availableEmploymentAgreements, setAvailableEmploymentAgreements] =
		useState([] as Array<string>);
	const [holidayVisaEmploymentAgreements, setHolidayVisaEmploymentAgreements] =
		useState([] as Array<string>);
	const [defaultEmploymentAgreements, setDefaultEmploymentAgreements] =
		useState([] as Array<string>);

	const [candidateSites, setCandidateSites] = useState(
		[] as Array<CandidateSite>
	);

	const [daysOfWorkPerWeek, setDaysOfWorkPerWeek] = useState(0);
	const [requiredInExcessOf10HoursPerDay, setRequiredInExcessOf10HoursPerDay] =
		useState(false);
	const [fixedTermEndDate, setFixedTermEndDate] = useState(null as any);
	const [startDate, setStartDate] = useState(null as any);
	const [managerName, setManagerName] = useState("");
	const [managerContactNumber, setManagerContactNumber] = useState("");
	const [hiringManagerName, setHiringManagerName] = useState("");
	const [hiringManagerContactNumber, setHiringManagerContactNumber] =
		useState("");
	const [ppcsConfirmed, setPpcsConfirmed] = useState(false);

	const [selectedTaskSets, setSelectedTaskSets] = useState([] as Array<string>);

	const [isSaving, setIsSaving] = useState(false);

	const [candidateCreated, setCandidateCreated] = useState(false);
	const [candidate, setCandidate] = useState(null as any);
	const [adminUsers, setAdminUsers] = useState([] as any);
	const [allocatedTo, setAllocatedTo] = useState<any>([]);
	const [workRegion, setWorkRegion] = useState("");
	const [department, setDepartment] = useState<any>([]);

	interface FieldErrors {
		[key: string]: string;
	}

	const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
		hiringManagerName: "",
		hiringManagerContactNumber: "",
		firstName: "",
		lastName: "",
		contactEmail: "",
		previouslyWorkedPinNumber: "",
		previouslyWorkedComment: "",
		position: "",
		positionDescriptionValue: "",
		hourlyRate: "",
		customHourlyRateValue: "",
		customHourlyRateReason: "",
		employmentTypeValue: "",
		approvedByRegionalManager: "",
		candidateSites: "",
		// siteJobCode: "",
		// siteJobCodeRate: "",
		// siteCustomJobCodeRate: "",
		// siteCustomJobCodeRateReason: "",	
		// startDate: "",
		// fixedTermEndDate: "",
		managerName: "",
		managerContactNumber: "",
		allocatedTo: "",
		ppcsConfirmed: "",
		workRegion: "",
		department: "",
	});

	const [requiredFields, setRequiredFields] = useState([
		"hiringManagerName",
		"hiringManagerContactNumber",
		"firstName",
		"lastName",
		"contactEmail",
		"previouslyWorkedPinNumber",
		"previouslyWorkedComment",
		"position",
		"positionDescriptionValue",
		"hourlyRate",
		"customHourlyRateValue",
		"customHourlyRateReason",
		"employmentTypeValue",
		"approvedByRegionalManager",
		"candidateSites[0].jobCode",
		"candidateSites[0].jobCodeRate",
		"managerName",
		"managerContactNumber",
		"allocatedTo",
		"ppcsConfirmed",
		"workRegion",
		"department",
	]);

	const addCandidateSiteFields = (index: number) => {
		const candidateSiteFields = [
			`candidateSites[${index}].jobCode`,
			`candidateSites[${index}].jobCodeRate`,
		];

		const newRequiredFields = [...requiredFields, ...candidateSiteFields];
		setRequiredFields(newRequiredFields);
	};

	const validateForm = () => {
		const errors: { [key: string]: string } = {};

		if (!hiringManagerName.trim()) {
			errors.hiringManagerName = "Hiring Manager Name is required";
		}

		if (!hiringManagerContactNumber.trim()) {
			errors.hiringManagerContactNumber =
				"Hiring Manager Contact Number is required";
		}

		if (!firstName.trim()) {
			errors.firstName = "First Name is required";
		}

		if (!lastName.trim()) {
			errors.lastName = "Last Name is required";
		}

		if (!contactEmail.trim()) {
			errors.contactEmail = "Email is required";
		}

		if (previouslyWorked && previouslyWorkedPinNumber === 0) {
			errors.previouslyWorkedPinNumber = "Previous PPCS pin number is required";
		}

		if (previouslyWorked && !previouslyWorkedComment.trim()) {
			errors.previouslyWorkedComment =
				"Reason for previously leaving PPCS is required";
		}

		if (position.length == 0) {
			errors.position = "Position is required";
		}

		if (positionDescriptionValue.length === 0) {
			errors.positionDescriptionValue = "Job Description is required";
		}

		if (!customHourlyRate && hourlyRate.length === 0) {
			errors.hourlyRate = "Hourly Rate is required";
		}

		if (customHourlyRate && customHourlyRateValue === 0) {
			errors.customHourlyRateValue = "Custom Hourly Rate is required";
		}

		if (customHourlyRate && !customHourlyRateReason.trim()) {
			errors.customHourlyRateReason =
				"Reason for Custom Hourly Rate is required";
		}

		if (employmentTypeValue.length == 0) {
			errors.employmentTypeValue = "Employment type is required";
		}

		if (!workRegion.trim()) {
			errors.workRegion = "Work region is required";
		}

		if (department.length == 0) {
			errors.department = "Department is required";
		}

		if (!approvedByRegionalManager) {
			errors.approvedByRegionalManager = "Regional Manager is required";
		}

		if (!candidateSites) {
			errors.candidateSites = "Candidate site information is required";
		}
		candidateSites.forEach((site, index) => {
			if (!site.jobCode?.trim()) {
				errors[`candidateSites[${index}].jobCode`] = `Job Code for Site ${
					index + 1
				} is required`;
			}
			if (typeof site.jobCodeRate !== "number" || isNaN(site.jobCodeRate)) {
				errors[
					`candidateSites[${index}].jobCodeRate`
				] = `Hourly Rate for Site ${index + 1} is required`;
			}
			if (site.customHourlyRate && !site.reasonForOtherRate?.trim()) {
				errors[
					`candidateSites[${index}].reasonForOtherRate`
				] = `Reason for Custom Hourly Rate on Site ${index + 1} is required`;
			}
			// Add additional validation for the "reasonForOtherRate" field if needed
		});

		candidateSites.forEach((site, index) => {
			if (!site.jobCode?.trim()) {
				errors[`candidateSites[${index}].jobCode`] = `Job Code for Site ${
					index + 1
				} is required`;
			}
			if (typeof site.jobCodeRate !== "number" || isNaN(site.jobCodeRate)) {
				errors[
					`candidateSites[${index}].jobCodeRate`
				] = `Hourly Rate for Site ${index + 1} is required`;
			}
			if (site.customHourlyRate && !site.reasonForOtherRate?.trim()) {
				errors[
					`candidateSites[${index}].reasonForOtherRate`
				] = `Reason for Custom Hourly Rate on Site ${index + 1} is required`;
			}
		});
		// if (!startDate) {
		// 	errors.startDate = "Start Date is required";
		// }

		// if (!fixedTermEndDate) {
		// 	errors.fixedTermEndDate = "Fixed Term End Date is required";
		// }

		if (!managerName.trim()) {
			errors.managerName = "Supervisor / Manager Name is required";
		}

		if (!managerContactNumber.trim()) {
			errors.managerContactNumber =
				"Supervisor / Manager Contact Number is required";
		}

		if (allocatedTo.length === 0) {
			errors.allocatedTo = "Allocated To is required";
		}

		if (!ppcsConfirmed) {
			errors.ppcsConfirmed =
				"Please confirm that correct details have been loaded";
		}

		setFieldErrors({
			...fieldErrors,
			...errors,
		});
		return Object.keys(errors).length === 0;
	};

	const validateAndSave = async () => {
		const isFormValid = validateForm();

		if (isFormValid) {
			await saveCandidate();
		} else {
			toaster.error("Please fill in all required fields");
		}
	};

	const getAdminUsers = async () => {
		let result = await request(
			"GET",
			`/api/admin/utility/admin-users`,
			null,
			true,
			false
		);
		if (result.success && result.data?.users) {
			setAdminUsers(result.data.users);
		} else {
			toaster.error("Unable to load admin users");
		}
	};

	const saveCandidate = async () => {
		setIsSaving(true);

		let result = await request(
			"POST",
			"/api/admin/candidate/create",
			{
				candidateInternal: {
					firstName,
					lastName,
					contactEmail,
					contactMobile,
					replacingName,
					replacingTerminated,
					previouslyWorked,
					previouslyWorkedComment,
					previouslyWorkedPinNumber,
					availableFullTime,
					availablePartTime,
					partTimeConfirm,
					hepBMonitoringRequired,
					drivingCompanyVehicle,
					kerbsideCollection,
					waterBlasting,
					workingAtPublicSchool,
					workingAtPublicSchoolWeeks:
						workingAtPublicSchoolWeeksValue.length == 1
							? workingAtPublicSchoolWeeksValue[0].value
							: 0,
					approvedByRegionalManager,
					employmentType:
						employmentTypeValue.length == 1
							? employmentTypeValue[0].value
							: null,
					workRegion,
					department: department.length == 1 ? department[0].value : null,
					departmentName: department.length == 1 ? department[0].label : null,
					availableEmploymentAgreements,
					isWorkingHoliday,
					isTOU,
					candidateType: "PPCS", //HN Placeholder for splitting to PIL or other candidate types.
				},
				candidateAddendum: {
					position: position.length == 1 ? position[0].value : null,
					hourlyRate: customHourlyRate // If custom hourly rate is selected, use that value, otherwise use the value from the dropdown
						? customHourlyRateValue
						: hourlyRate.length === 1
						? hourlyRate[0].jobCodeRate
						: 0,
					reasonForOtherRate: customHourlyRate ? customHourlyRateReason : null,
					reasonForAgreement,
					startDate: zonelessIsoDate(dateFromDateArray(startDate)),
					fixedTermEndDate: zonelessIsoDate(
						dateFromDateArray(fixedTermEndDate)
					),
					managerName,
					managerContactNumber,
					ppcsConfirmed,
					daysOfWorkPerWeek,
					requiredInExcessOf10HoursPerDay,
					positionDescriptionFileId:
						positionDescriptionValue.length == 1
							? positionDescriptionValue[0].id
							: null,
					hiringManagerName,
					hiringManagerContactNumber,
				},
				candidate: {
					allocatedTo: allocatedTo.length == 1 ? allocatedTo[0].id : null,
				},
				candidateSites,
				selectedTaskSets,
			},
			true,
			false
		);
		if (result.success) {
			setCandidate(result.data);
			setCandidateCreated(true);
			allDone();
		} else if (result.message) {
			toaster.error(result.message);
		} else {
			toaster.error("An error occured while saving");
		}
		setIsSaving(false);
	};

	const allDone = () => {
		navigate({
			pathname: `/candidates`,
		});
	};

	const holidayVisaEmploymentAgreementsSelect = async () => {
		//reset all
		setAvailableEmploymentAgreements([]);
	};

	const defaultEmploymentAgreementsSelect = async () => {
		//default selection
		let tmpAvailableEmploymentAgreements = defaultEmploymentAgreements;

		for (let defEmploymentAgreement in availableEmploymentAgreements) {
			if (
				tmpAvailableEmploymentAgreements.indexOf(defEmploymentAgreement) > -1
			) {
				tmpAvailableEmploymentAgreements.splice(
					tmpAvailableEmploymentAgreements.indexOf(defEmploymentAgreement),
					1
				);
			} else {
				tmpAvailableEmploymentAgreements.push(defEmploymentAgreement);
			}
		}

		setAvailableEmploymentAgreements(tmpAvailableEmploymentAgreements);
	};

	const getLabel = ({ option }: any) => {
		return (
			<React.Fragment>
				{`${option.jobCode} - ${option.jobCodeName}`}
			</React.Fragment>
		);
	};

	const getSiteJobCodeError = (index: number) => {
		const key = `candidateSites[${index}].jobCode`;
		return fieldErrors[key];
	};

	const getSiteJobCodeRateError = (index: number) => {
		const key = `candidateSites[${index}].jobCodeRate`;
		return fieldErrors[key];
	};

	const getSiteReasonForCustomRateError = (index: number) => {
		const key = `candidateSites[${index}].reasonForOtherRate`;
		return fieldErrors[key];
	};

	const nzHours = (date: Date) => {
		return new Date(date.setHours(date.getHours() + 12));// PC: UTC Time to NZT
	};

	return (
		<Container>
			<h1>Create PPCS Candidate</h1>
			<hr />
			<TwoColumnGrid>
				<div>
					<FormControl
						label={
							<label
								className={
									requiredFields.includes("hiringManagerName") ? "required" : ""
								}
							>
								Hiring Manager name
							</label>
						}
						error={fieldErrors.hiringManagerName}
					>
						<Input
							value={hiringManagerName}
							onChange={(e) => {
								setHiringManagerName(e.currentTarget.value);
							}}
						/>
					</FormControl>
				</div>
				<div>
					<FormControl
						label={
							<label
								className={
									requiredFields.includes("hiringManagerContactNumber")
										? "required"
										: ""
								}
							>
								Hiring Manager contact number
							</label>
						}
						error={fieldErrors.hiringManagerContactNumber}
					>
						<Input
							value={hiringManagerContactNumber}
							onChange={(e) => {
								setHiringManagerContactNumber(e.currentTarget.value);
							}}
						/>
					</FormControl>
				</div>
			</TwoColumnGrid>
			<FormControl
				label={
					<label
						className={requiredFields.includes("firstName") ? "required" : ""}
					>
						First name
					</label>
				}
				error={fieldErrors.firstName}
			>
				<Input
					value={firstName}
					onChange={(e) => {
						setFirstName(e.currentTarget.value);
					}}
				/>
			</FormControl>
			<FormControl
				label={
					<label
						className={requiredFields.includes("lastName") ? "required" : ""}
					>
						Last name
					</label>
				}
				error={fieldErrors.lastName}
			>
				<Input
					value={lastName}
					onChange={(e) => {
						setLastName(e.currentTarget.value);
					}}
				/>
			</FormControl>
			<FormControl
				label={
					<label
						className={
							requiredFields.includes("contactEmail") ? "required" : ""
						}
					>
						Email
					</label>
				}
				error={fieldErrors.contactEmail}
			>
				<Input
					value={contactEmail}
					onChange={(e) => {
						setContactEmail(e.currentTarget.value);
					}}
				/>
			</FormControl>
			<FormControl label={"Mobile phone number"}>
				<Input
					value={contactMobile}
					onChange={(e) => {
						setContactMobile(e.currentTarget.value);
					}}
				/>
			</FormControl>
			<hr />
			<TwoColumnGrid>
				<div>
					<FormControl
						label={"Is this a Transfer of undertakings (TOU) candidate?"}
					>
						<Checkbox
							checked={isTOU}
							onChange={() => {
								setIsTOU(!isTOU);
							}}
						/>
					</FormControl>
				</div>
				<div>
					<FormControl label={"Is the candidate available full time?"}>
						<Checkbox
							checked={availableFullTime}
							onChange={() => {
								setAvailableFullTime(!availableFullTime);
							}}
						/>
					</FormControl>
				</div>
				<div>
					<FormControl label={"Is the candidate available part time?"}>
						<Checkbox
							checked={availablePartTime}
							onChange={() => {
								setAvailablePartTime(!availablePartTime);
							}}
						/>
					</FormControl>
				</div>
				<div>
					<FormControl
						label={
							"Is the candidate willing to go ahead even if only part-time work is available?"
						}
					>
						<Checkbox
							checked={partTimeConfirm}
							onChange={() => {
								setPartTimeConfirm(!partTimeConfirm);
							}}
						/>
					</FormControl>
				</div>
				<div>
					<FormControl
						label={"Is the candidate holding a working holiday visa?"}
					>
						<Checkbox
							checked={isWorkingHoliday}
							onChange={() => {
								if (!isWorkingHoliday) {
									// This becomes true when checking this question
									holidayVisaEmploymentAgreementsSelect();
								} else {
									defaultEmploymentAgreementsSelect();
								}

								setIsWorkingHoliday(!isWorkingHoliday);
							}}
						/>
					</FormControl>
				</div>
				<div>
					<FormControl label={"Has the candidate previously worked for PPCS?"}>
						<Checkbox
							checked={previouslyWorked}
							onChange={() => {
								setPreviouslyWorked(!previouslyWorked);
							}}
						/>
					</FormControl>
					{previouslyWorked && (
						<>
							<div>
								<FormControl
									label={
										<label
											className={
												requiredFields.includes("previouslyWorkedPinNumber")
													? "required"
													: ""
											}
										>
											Previous pin number to be reactivated:
										</label>
									}
									error={fieldErrors.previouslyWorkedPinNumber}
								>
									<Input
										type="Number"
										onChange={(e) => {
											setPreviouslyWorkedPinNumber(
												e.currentTarget.valueAsNumber
											);
										}}
									/>
								</FormControl>
								<FormControl
									label={
										<label
											className={
												requiredFields.includes("previouslyWorkedComment")
													? "required"
													: ""
											}
										>
											Why did the candidate leave?
										</label>
									}
									error={fieldErrors.previouslyWorkedComment}
								>
									<Input
										onChange={(e) => {
											setPreviouslyWorkedComment(e.currentTarget.value);
										}}
									/>
								</FormControl>
							</div>
						</>
					)}
				</div>
			</TwoColumnGrid>

			<hr />
			<FormControl label={"Who are they replacing?"}>
				<Input
					value={replacingName}
					onChange={(e) => {
						setReplacingName(e.currentTarget.value);
					}}
				/>
			</FormControl>
			<FormControl label={"Has that person been terminated?"}>
				<Checkbox
					checked={replacingTerminated}
					onChange={() => {
						setReplacingTerminated(!replacingTerminated);
					}}
				/>
			</FormControl>
			<hr />

			<TwoColumnGrid>
				<div>
					<FormControl
						label={
							<label
								className={
									requiredFields.includes("position") ? "required" : ""
								}
							>
								Which position are they being employed for?
							</label>
						}
						error={fieldErrors.position}
					>
						<>
							<Select
								labelKey={"label"}
								valueKey={"value"}
								maxDropdownHeight="12rem"
								clearable={false}
								options={enums.jobPosition}
								value={position}
								onChange={(params) => {
									if (params && params.value) {
										setPosition(params.value);
									}
								}}
							/>
						</>
					</FormControl>
				</div>
				<div>
					<FormControl
						label={
							<label
								className={
									requiredFields.includes("positionDescriptionValue")
										? "required"
										: ""
								}
							>
							Note: Please select the correct JD - there is a difference between an ordinary <b>Cleaner</b> and a <b>Public Facilities Cleaner (PFC)</b><br />
							Which Job Description should be used?
							</label>
						}
						error={fieldErrors.positionDescriptionValue}
					>
						<>
							<Select
								labelKey={"name"}
								valueKey={"id"}
								options={allPositionDescriptions}
								maxDropdownHeight="12rem"
								clearable={false}
								value={positionDescriptionValue}
								onChange={(params) => {
									setPositionDescriptionValue(params.value);
								}}
							/>
							{positionDescriptionValue &&
								positionDescriptionValue.length == 1 && (
									<a
										href={positionDescriptionValue[0].fileLink}
										target="_blank"
									>
										<br />
										Download Job Description
									</a>
								)}
						</>
					</FormControl>
				</div>

				<div>
					<FormControl
						label="Will this candidate be cleaning public toilets where monitoring of Hep B is required?"
						caption="Some clients, for example Council Sites, require this"
					>
						<Checkbox
							checked={hepBMonitoringRequired}
							onChange={() => {
								let currentValue = hepBMonitoringRequired;
								setHepBMonitoringRequired(!hepBMonitoringRequired);

								if (!currentValue) {
									const hepBMonitoringSetId =
										allOnboardingTaskSets.items.filter((item: any) => {
											return item.code === "hepatitis-declaration";
										})[0].identifier;

									if (selectedTaskSets.indexOf(hepBMonitoringSetId) < 0) {
										let tmpSelectedTaskSets = [...selectedTaskSets];
										tmpSelectedTaskSets.push(hepBMonitoringSetId);
										setSelectedTaskSets(tmpSelectedTaskSets);
									}
								}
							}}
						/>
					</FormControl>
					{hepBMonitoringRequired && (
						<Notification>
							Remember to update the{" "}
							<a
								href={
									appContext.state.settings.HepBScreeningSheet ?? "about:blank"
								}
								target="_blank"
								rel="noopener"
							>
								HepB Screening Sheet
							</a>
						</Notification>
					)}
				</div>
				<div>
					<FormControl label="Will this candidate be driving a company vehicle?">
						<Checkbox
							checked={drivingCompanyVehicle}
							onChange={() => {
								let currentValue = drivingCompanyVehicle;
								setDrivingCompanyVehicle(!drivingCompanyVehicle);

								if (!currentValue) {
									const drivingAgreementSetId =
										allOnboardingTaskSets.items.filter((item: any) => {
											return item.code === "driving-policy";
										})[0].identifier;

									if (selectedTaskSets.indexOf(drivingAgreementSetId) < 0) {
										let tmpSelectedTaskSets = [...selectedTaskSets];
										tmpSelectedTaskSets.push(drivingAgreementSetId);
										setSelectedTaskSets(tmpSelectedTaskSets);
									}
								}
							}}
						/>
					</FormControl>
					{drivingCompanyVehicle && (
						<Notification>
							Remember to update{" "}
							<ol>
								<li>
									The{" "}
									<a
										href={
											appContext.state.settings.VehicleScheduleSheet ??
											"about:blank"
										}
										target="_blank"
										rel="noopener"
									>
										Vehicle Schedule Sheet
									</a>
								</li>
								<li>
									The{" "}
									<a
										href={
											appContext.state.settings.DriverCheckScheduleSheet ??
											"about:blank"
										}
										target="_blank"
										rel="noopener"
									>
										Driver Check Schedule Sheet
									</a>{" "}
									(so their details can be added to NZTA)
								</li>
							</ol>
						</Notification>
					)}
				</div>
				<div>
					<FormControl
						label="Will this candidate be doing Water Blasting on a contract?"
						caption="Where they will use the machine for most of their shift"
					>
						<Checkbox
							checked={waterBlasting}
							onChange={() => {
								let currentValue = waterBlasting;
								setWaterBlasting(!waterBlasting);

								if (!currentValue) {
									const waterBlastingSetId = allOnboardingTaskSets.items.filter(
										(item: any) => {
											return item.code === "hearing-test-declaration";
										}
									)[0].identifier;

									if (selectedTaskSets.indexOf(waterBlastingSetId) < 0) {
										let tmpSelectedTaskSets = [...selectedTaskSets];
										tmpSelectedTaskSets.push(waterBlastingSetId);
										setSelectedTaskSets(tmpSelectedTaskSets);
									}
								}
							}}
						/>
					</FormControl>
					{waterBlasting && (
						<Notification>
							Staff member will need to agree to the relevant terms and
							conditions
						</Notification>
					)}
				</div>
				<div>
					<FormControl label="Will this candidate be working at a school?">
						<Checkbox
							checked={workingAtPublicSchool}
							onChange={() => {
								let currentValue = workingAtPublicSchool;
								setWorkingAtPublicSchool(!workingAtPublicSchool);

								if (!currentValue) {
									const schoolCleanerTaskSetId =
										allOnboardingTaskSets.items.filter((item: any) => {
											return item.code === "school-cleaner";
										})[0].identifier;

									if (selectedTaskSets.indexOf(schoolCleanerTaskSetId) < 0) {
										let tmpSelectedTaskSets = [...selectedTaskSets];
										tmpSelectedTaskSets.push(schoolCleanerTaskSetId);
										setSelectedTaskSets(tmpSelectedTaskSets);
									}
								}
							}}
						/>
					</FormControl>
					{workingAtPublicSchool && (
						<Notification>
							Staff member will need to agree to the relevant terms and
							conditions
						</Notification>
					)}
				</div>
			</TwoColumnGrid>
			{workingAtPublicSchool && (
				<FormControl label="For the school that the candidate is working at, how many weeks will the supplementary School Cleaner agreement be for?">
					<>
						<Select
							labelKey={"label"}
							valueKey={"value"}
							options={enums.schoolCleanerWeeks}
							maxDropdownHeight="12rem"
							clearable={false}
							value={workingAtPublicSchoolWeeksValue}
							onChange={(params) => {
								setWorkingAtPublicSchoolWeeksValue(params.value);
							}}
						/>
					</>
				</FormControl>
			)}
			<TwoColumnGrid>
				<div>
					<FormControl
						label={
							<label
								className={
									requiredFields.includes("hourlyRate") ? "required" : ""
								}
							>
								Hourly Rate?
							</label>
						}
						error={fieldErrors.hourlyRate}
					>
						{jobCodeRates && (
							<StyledSelect
								labelKey={"jobCodeRate"}
								valueKey={"jobCodeRate"}
								options={jobCodeDefaultRate}
								maxDropdownHeight="12rem"
								clearable={true}
								value={customHourlyRate ? 0 : jobCodeDefaultRate}
								disabled={true}
								onChange={(params) => {
									setHourlyRate(params.value);
								}}
							/>
						)}
					</FormControl>
				</div>
				<div>
					<FormControl label="Custom hourly rate required">
						<Checkbox
							checked={customHourlyRate}
							onChange={() => {
								setCustomHourlyRate(!customHourlyRate);
							}}
						/>
					</FormControl>
				</div>
			</TwoColumnGrid>
			{customHourlyRate && (
				<TwoColumnGrid>
					<div>
						<FormControl
							label={
								<label
									className={
										customHourlyRate &&
										requiredFields.includes("customHourlyRateValue")
											? "required"
											: ""
									}
								>
									Custom Hourly Rate?
								</label>
							}
							error={fieldErrors.customHourlyRateValue}
						>
							<Input
								startEnhancer="$"
								value={customHourlyRateValue}
								onChange={(e) => {
									const currentValue = JSON.parse(
										JSON.stringify(customHourlyRateValue)
									);
									let newValue = parseFloat(
										parseFloat(e.currentTarget.value).toFixed(2)
									);
									setCustomHourlyRateValue(newValue);

									let tmpCandidateSites = [...candidateSites];

									tmpCandidateSites.map(
										(candidateSite: CandidateSite, position: number) => {
											if (
												candidateSite.jobCodeRate == null ||
												candidateSite.jobCodeRate == currentValue //Sets all sites' rates that are equal to default, to new default.
											) {
												(
													tmpCandidateSites[position] as CandidateSite
												).jobCodeRate = newValue;
											}
										}
									);

									setCandidateSites(tmpCandidateSites);
								}}
								type="number"
								min={0}
							/>
						</FormControl>
					</div>
					<div>
						<FormControl
							label={
								<label
									className={
										customHourlyRate &&
										requiredFields.includes("customHourlyRateReason")
											? "required"
											: ""
									}
								>
									Reason for Custom Rate
								</label>
							}
							error={fieldErrors.customHourlyRateReason}
						>
							<Input
								value={customHourlyRateReason}
								onChange={(e) => {
									setCustomHourlyRateReason(e.currentTarget.value);
								}}
							/>
						</FormControl>
					</div>
				</TwoColumnGrid>
			)}
				<TwoColumnGrid>
					<div>
					<FormControl
						label={
							<label
								className={requiredFields.includes("workRegion") ? "required" : ""}
							>
								Work Region
							</label>
						}
						error={fieldErrors.workRegion}
					>
						<>
							<RadioGroup
								value={workRegion}
								onChange={(e) => setWorkRegion(e.currentTarget.value)}
								align={ALIGN.horizontal}
							>
								{enums.WorkRegions &&
									enums.WorkRegions.map((WorkRegions: any) => {
										return (
											<Radio value={WorkRegions.value}>{WorkRegions.label}</Radio>
										);
									})}
							</RadioGroup>
						</>
					</FormControl>
					</div>
					<div>
					<FormControl
						label={
							<label
								className={
									requiredFields.includes("department") ? "required" : ""
								}
							>
								Which department is the candidate under?
							</label>
						}
						error={fieldErrors.department}
					>
						<>
							<Select
								//required
								options={allDepartments.map((department: any) => {
									return {
										value: department.id,
										label: department.region.name +
										" - " +
										department.name,
									};
								})}
								labelKey={"label"}
								valueKey={"value"}
								maxDropdownHeight="12rem"
								value={department}
								clearable={false}
								onChange={(params) => {
									setDepartment(params.value);
								}}
							/>
						</>
					</FormControl>
					</div>
				</TwoColumnGrid>

			<FormControl
				label={
					<label
						className={
							requiredFields.includes("employmentTypeValue") ? "required" : ""
						}
					>
						Employment type
					</label>
				}
				error={fieldErrors.employmentTypeValue}
			>
				<>
					<Select
						labelKey={"label"}
						valueKey={"value"}
						maxDropdownHeight="12rem"
						clearable={false}
						options={
							isWorkingHoliday
								? enums.employmentType.filter(
										(etype: { label: string }) => etype.label != "Permanent"
								  )
								: enums.employmentType
						}
						value={employmentTypeValue}
						onChange={(params) => {
							if (params && params.value) {
								setEmploymentTypeValue(params.value);
							}

						// default agreements based on employment type
						let tmpAvailableEmploymentAgreements = [] as Array<string>;

						let employmenttypeLabel = params.value[0].label as string;
						let employmentTypeSplit = employmenttypeLabel.split("-");

						let positionTemp;
						try{positionTemp=employmentTypeSplit[0];}
						catch(error) {positionTemp = "";}
							for (let employmentAgreement in allEmploymentAgreements) {
								if(positionTemp == "Maintenance Technician")
								{
									if(allEmploymentAgreements[employmentAgreement].name.includes("Individual Employment Agreement"))
										{
											tmpAvailableEmploymentAgreements.push(
												allEmploymentAgreements[employmentAgreement].identifier
											);
										}
								}
								else
								if(positionTemp == "Grounds Maintenance Operator")
								{
									if(allEmploymentAgreements[employmentAgreement].name.includes(employmentTypeSplit[0]) && allEmploymentAgreements[employmentAgreement].name.includes(employmentTypeSplit[1]) && employmentTypeSplit.length==2)
										{
											tmpAvailableEmploymentAgreements.push(
												allEmploymentAgreements[employmentAgreement].identifier
											);
										}
								}
								else
								{
									if (allEmploymentAgreements[employmentAgreement].name.includes(params.value[0].label) && !allEmploymentAgreements[employmentAgreement].name.includes("Grounds Maintenance Operator")) {
										tmpAvailableEmploymentAgreements.push(
											allEmploymentAgreements[employmentAgreement].identifier
										);
									}
								}
							}

							if (tmpAvailableEmploymentAgreements.length > 0) {
								setAvailableEmploymentAgreements(tmpAvailableEmploymentAgreements);
							}
							else {
								setAvailableEmploymentAgreements(defaultEmploymentAgreements);
							}
						}}
					/>
				</>
			</FormControl>
			<TwoColumnGrid>
				{allEmploymentAgreements &&
					allEmploymentAgreements.length > 0 &&
					allEmploymentAgreements.map((employmentAgreement: any) => {
						return (
							<>
								<div>
									<FormControl
										label={`Offer ${employmentAgreement.name}`}
										caption={employmentAgreement.notes}
										overrides={{
											Label: {
												style: () => ({
													opacity: isWorkingHoliday
														? holidayVisaEmploymentAgreements.indexOf(
																employmentAgreement.identifier
														  ) > -1
															? "1"
															: "0.3"
														: "1",
												}),
											},
											Caption: {
												style: () => ({
													opacity: isWorkingHoliday
														? holidayVisaEmploymentAgreements.indexOf(
																employmentAgreement.identifier
														  ) > -1
															? "1"
															: "0.3"
														: "1",
												}),
											},
										}}
									>
										<>
											<Checkbox
												checked={
													availableEmploymentAgreements.indexOf(
														employmentAgreement.identifier
													) > -1
														? (workRegion == "Southern" || workRegion == "Central") && 
														  employmentAgreement.name.includes("AWUNZ")
															? false
															: true
														: false
												}
												onChange={() => {
													let tmpAvailableEmploymentAgreements = [
														...availableEmploymentAgreements,
													];

													if (
														tmpAvailableEmploymentAgreements.indexOf(
															employmentAgreement.identifier
														) > -1
													) {
														tmpAvailableEmploymentAgreements.splice(
															tmpAvailableEmploymentAgreements.indexOf(
																employmentAgreement.identifier
															),
															1
														);
													} else {
														tmpAvailableEmploymentAgreements.push(
															employmentAgreement.identifier
														);
													}

													setAvailableEmploymentAgreements(
														tmpAvailableEmploymentAgreements
													);
												}}
												disabled={
													isWorkingHoliday
														? holidayVisaEmploymentAgreements.indexOf(
																employmentAgreement.identifier
														  ) > -1
															? false
															: true
														: false
												}
											/>
										</>
									</FormControl>
								</div>

								{employmentAgreement.slug ==
									"individual-employment-agreement" && <div> </div>}
							</>
						);
					})}
			</TwoColumnGrid>
			<FormControl
				label={<label>Reason for Fixed Term Employment Agreement</label>}
			>
				<Input
					value={reasonForAgreement}
					onChange={(e) => {
						setReasonForAgreement(e.currentTarget.value);
					}}
				/>
			</FormControl>
			<FormControl
				label={
					<label
						className={
							requiredFields.includes("approvedByRegionalManager")
								? "required"
								: ""
						}
					>
						Approved by (Operation Manager: if replacing an existing position where staff member has either been terminated, promoted or transferred. Regional Manager if newly created position)
					</label>
				}
				error={fieldErrors.approvedByRegionalManager}
			>
				<Input
					value={approvedByRegionalManager}
					onChange={(e) => {
						setApprovedByRegionalManager(e.currentTarget.value);
					}}
				/>
			</FormControl>
			<hr />
			{candidateSites.map((candidateSite: CandidateSite) => {
				return (
					<>
						{candidateSite.siteId > 0 && <hr />}
						<FormControl
							label={
								<label
									className={
										requiredFields.includes(
											`candidateSites[${candidateSite.siteId - 1}].jobCode`
										)
											? "required"
											: ""
									}
								>
									{`Site ${candidateSite.siteId} - Job Code`}
								</label>
							}
							error={getSiteJobCodeError(candidateSite.siteId - 1)}
						>
							{jobCodes && (
								<StyledSelect
									labelKey={"jobCodeName"}
									valueKey={"jobCode"}
									options={jobCodes}
									maxDropdownHeight="12rem"
									clearable={true}
									value={jobCodes.find(
										(jc: { jobCode: string }) =>
											jc.jobCode === candidateSite.jobCode
									)}
									onChange={(params) => {
										let sites = candidateSites.slice();
										sites[candidateSite.siteId - 1].jobCode =
											params.option?.jobCode;
										setCandidateSites(sites);
									}}
									getOptionLabel={getLabel}
									getValueLabel={getLabel}
								/>
							)}
						</FormControl>
						<TwoColumnGrid>
							<div>
								<FormControl
									label={
										<label
											className={
												candidateSite.customHourlyRate == false &&
												requiredFields.includes(
													`candidateSites[${
														candidateSite.siteId - 1
													}].jobCodeRate`
												)
													? "required"
													: ""
											}
										>
											{`Site ${candidateSite.siteId} - Hourly Rate`}
										</label>
									}
									error={
										candidateSite.customHourlyRate == false &&
										getSiteJobCodeRateError(candidateSite.siteId - 1)
									}
								>
									{jobCodeRates && (
										<StyledSelect
											labelKey={"jobCodeRate"}
											valueKey={"jobCodeRate"}
											options={jobCodeRates}
											maxDropdownHeight="12rem"
											clearable={true}
											value={jobCodeRates.filter(
												(jc: { jobCodeRate: number }) =>
													jc.jobCodeRate == candidateSite.jobCodeRate
											)}
											disabled={candidateSite.customHourlyRate}
											onChange={(params) => {
												let sites = candidateSites.slice();
												sites[candidateSite.siteId - 1].jobCodeRate =
													params.option?.jobCodeRate;
												setCandidateSites(sites);
											}}
										/>
									)}
								</FormControl>
							</div>
							<div>
								<FormControl label="Custom hourly rate required">
									<Checkbox
										checked={candidateSite.customHourlyRate}
										onChange={(e) => {
											let sites = [...candidateSites];
											sites[candidateSite.siteId - 1].customHourlyRate =
												e.currentTarget.checked;

											// Include or exclude reasonForOtherRate in requiredFields based on the checkbox state
											if (e.currentTarget.checked) {
												requiredFields.push(
													`candidateSites[${
														candidateSite.siteId - 1
													}].reasonForOtherRate`
												);
											} else {
												const indexToRemove = requiredFields.indexOf(
													`candidateSites[${
														candidateSite.siteId - 1
													}].reasonForOtherRate`
												);
												if (indexToRemove !== -1) {
													requiredFields.splice(indexToRemove, 1);
												}
											}

											setCandidateSites(sites);
										}}
									/>
								</FormControl>
							</div>
						</TwoColumnGrid>
						{candidateSite.customHourlyRate && (
							<TwoColumnGrid>
								<div>
									<FormControl
										label={
											<label
												className={
													candidateSite.customHourlyRate &&
													requiredFields.includes(
														`candidateSites[${
															candidateSite.siteId - 1
														}].jobCodeRate`
													)
														? "required"
														: ""
												}
											>
												{`Site ${candidateSite.siteId} - Custom Hourly Rate?`}
											</label>
										}
										error={
											candidateSite.customHourlyRate &&
											getSiteJobCodeRateError(candidateSite.siteId - 1)
										}
									>
										<Input
											startEnhancer="$"
											value={candidateSite.jobCodeRate}
											//value={candidateSite.jobCode ? jobCodes.filter((jc: { jobCode: string; }) => jc.jobCode === candidateSite.jobCode) : []}
											onChange={(e) => {
												let sites = candidateSites.slice();
												sites[candidateSite.siteId - 1].jobCodeRate =
													parseFloat(e.currentTarget.value);
												setCandidateSites(sites);
											}}
											type="number"
											min={0}
										/>
									</FormControl>
								</div>
								<div>
									<FormControl
										label={
											<label
												className={
													requiredFields.includes(
														`candidateSites[${
															candidateSite.siteId - 1
														}].reasonForOtherRate`
													)
														? "required"
														: ""
												}
											>
												{`Site ${candidateSite.siteId} - Reason for Custom Rate`}
											</label>
										}
										error={getSiteReasonForCustomRateError(
											candidateSite.siteId - 1
										)}
									>
										<Input
											value={candidateSite.reasonForOtherRate}
											onChange={(e) => {
												let sites = candidateSites.slice();
												sites[candidateSite.siteId - 1].reasonForOtherRate =
													e.currentTarget.value;
												setCandidateSites(sites);
											}}
										/>
									</FormControl>
								</div>
							</TwoColumnGrid>
						)}
						<div>
							<FormControl
								label={`Site ${candidateSite.siteId}  HOURS per day, exclusive of meal break (total paid hours)`}
							>
								<SiteDaysContainer>
									<div>
										<FormControl label="Monday">
											<Input
												type="number"
												min={0}
												value={candidateSite.mondayHours}
												endEnhancer="hours"
												onChange={(e) => {
													let sites = candidateSites.slice();
													sites[candidateSite.siteId - 1].mondayHours =
														parseFloat(e.currentTarget.value);
													setCandidateSites(sites);
												}}
											/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Tuesday">
											<Input
												type="number"
												min={0}
												value={candidateSite.tuesdayHours}
												endEnhancer="hours"
												onChange={(e) => {
													let sites = candidateSites.slice();
													sites[candidateSite.siteId - 1].tuesdayHours =
														parseFloat(e.currentTarget.value);
													setCandidateSites(sites);
												}}
											/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Wednesday">
											<Input
												type="number"
												min={0}
												value={candidateSite.wednesdayHours}
												endEnhancer="hours"
												onChange={(e) => {
													let sites = candidateSites.slice();
													sites[candidateSite.siteId - 1].wednesdayHours =
														parseFloat(e.currentTarget.value);
													setCandidateSites(sites);
												}}
											/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Thursday">
											<Input
												type="number"
												min={0}
												value={candidateSite.thursdayHours}
												endEnhancer="hours"
												onChange={(e) => {
													let sites = candidateSites.slice();
													sites[candidateSite.siteId - 1].thursdayHours =
														parseFloat(e.currentTarget.value);
													setCandidateSites(sites);
												}}
											/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Friday">
											<Input
												type="number"
												min={0}
												value={candidateSite.fridayHours}
												endEnhancer="hours"
												onChange={(e) => {
													let sites = candidateSites.slice();
													sites[candidateSite.siteId - 1].fridayHours =
														parseFloat(e.currentTarget.value);
													setCandidateSites(sites);
												}}
											/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Saturday">
											<Input
												type="number"
												min={0}
												value={candidateSite.saturdayHours}
												endEnhancer="hours"
												onChange={(e) => {
													let sites = candidateSites.slice();
													sites[candidateSite.siteId - 1].saturdayHours =
														parseFloat(e.currentTarget.value);
													setCandidateSites(sites);
												}}
											/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Sunday">
											<Input
												type="number"
												min={0}
												endEnhancer="hours"
												value={candidateSite.sundayHours}
												onChange={(e) => {
													let sites = candidateSites.slice();
													sites[candidateSite.siteId - 1].sundayHours =
														parseFloat(e.currentTarget.value);
													setCandidateSites(sites);
												}}
											/>
										</FormControl>
									</div>
								</SiteDaysContainer>
							</FormControl>
						</div>
						<div>
							<FormControl
								label={`Site ${candidateSite.siteId} START TIME per day.`}
							>
								<SiteDaysContainer>
									<div>
										<FormControl label="Monday Start Time">
												<TimePicker
													onChange={date => {
														let sites = candidateSites.slice();
														sites[candidateSite.siteId - 1].mondayStartTime = date
															? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
															: "";
														setCandidateSites(sites);
													}}
													nullable
													format="24"
													value={candidateSite.mondayStartTime && /^\d{2}:\d{2}$/.test(candidateSite.mondayStartTime) ? new Date(`1970-01-01T${candidateSite.mondayStartTime}`) : null}
												/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Tuesday Start Time">
												<TimePicker
													onChange={date => {
														let sites = candidateSites.slice();
														sites[candidateSite.siteId - 1].tuesdayStartTime = date
															? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
															: "";
														setCandidateSites(sites);
													}}
													nullable
													format="24"
													value={candidateSite.tuesdayStartTime && /^\d{2}:\d{2}$/.test(candidateSite.tuesdayStartTime) ? new Date(`1970-01-01T${candidateSite.tuesdayStartTime}`) : null}
												/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Wednesday Start Time">
												<TimePicker
													onChange={date => {
														let sites = candidateSites.slice();
														sites[candidateSite.siteId - 1].wednesdayStartTime = date
															? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
															: "";
														setCandidateSites(sites);
													}}
													nullable
													format="24"
													value={candidateSite.wednesdayStartTime && /^\d{2}:\d{2}$/.test(candidateSite.wednesdayStartTime) ? new Date(`1970-01-01T${candidateSite.wednesdayStartTime}`) : null}
												/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Thursday Start Time">
												<TimePicker
													onChange={date => {
														let sites = candidateSites.slice();
														sites[candidateSite.siteId - 1].thursdayStartTime = date
															? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
															: "";
														setCandidateSites(sites);
													}}
													nullable
													format="24"
													value={candidateSite.thursdayStartTime && /^\d{2}:\d{2}$/.test(candidateSite.thursdayStartTime) ? new Date(`1970-01-01T${candidateSite.thursdayStartTime}`) : null}
												/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Friday Start Time">
												<TimePicker
													onChange={date => {
														let sites = candidateSites.slice();
														sites[candidateSite.siteId - 1].fridayStartTime = date
															? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
															: "";
														setCandidateSites(sites);
													}}
													nullable
													format="24"
													value={candidateSite.fridayStartTime && /^\d{2}:\d{2}$/.test(candidateSite.fridayStartTime) ? new Date(`1970-01-01T${candidateSite.fridayStartTime}`) : null}
												/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Saturday Start Time">
												<TimePicker
													onChange={date => {
														let sites = candidateSites.slice();
														sites[candidateSite.siteId - 1].saturdayStartTime = date
															? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
															: "";
														setCandidateSites(sites);
													}}
													nullable
													format="24"
													value={candidateSite.saturdayStartTime && /^\d{2}:\d{2}$/.test(candidateSite.saturdayStartTime) ? new Date(`1970-01-01T${candidateSite.saturdayStartTime}`) : null}
												/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Sunday Start Time">
												<TimePicker
													onChange={date => {
														let sites = candidateSites.slice();
														sites[candidateSite.siteId - 1].sundayStartTime = date
															? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
															: "";
														setCandidateSites(sites);
													}}
													nullable
													format="24"
													value={candidateSite.sundayStartTime && /^\d{2}:\d{2}$/.test(candidateSite.sundayStartTime) ? new Date(`1970-01-01T${candidateSite.sundayStartTime}`) : null}
												/>
										</FormControl>
									</div>
								</SiteDaysContainer>
							</FormControl>
						</div>
						 <div>
							<FormControl
								label={`Site ${candidateSite.siteId} END TIME per day. Any shift that is longer than 4 hours must take into account a 30 minute unpaid meal break. e.g. 8:00am to 1:00pm is 4.5 hours paid.`}
							>
								<SiteDaysContainer>
									<div>
										<FormControl label="Monday End Time">
												<TimePicker
													onChange={date => {
														let sites = candidateSites.slice();
														sites[candidateSite.siteId - 1].mondayEndTime = date
															? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
															: "";
														setCandidateSites(sites);
													}}
													nullable
													format="24"
													value={candidateSite.mondayEndTime && /^\d{2}:\d{2}$/.test(candidateSite.mondayEndTime) ? new Date(`1970-01-01T${candidateSite.mondayEndTime}`) : null}
												/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Tuesday End Time">
												<TimePicker
													onChange={date => {
														let sites = candidateSites.slice();
														sites[candidateSite.siteId - 1].tuesdayEndTime = date
															? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
															: "";
														setCandidateSites(sites);
													}}
													nullable
													format="24"
													value={candidateSite.tuesdayEndTime && /^\d{2}:\d{2}$/.test(candidateSite.tuesdayEndTime) ? new Date(`1970-01-01T${candidateSite.tuesdayEndTime}`) : null}
												/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Wednesday End Time">
												<TimePicker
													onChange={date => {
														let sites = candidateSites.slice();
														sites[candidateSite.siteId - 1].wednesdayEndTime = date
															? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
															: "";
														setCandidateSites(sites);
													}}
													nullable
													format="24"
													value={candidateSite.wednesdayEndTime && /^\d{2}:\d{2}$/.test(candidateSite.wednesdayEndTime) ? new Date(`1970-01-01T${candidateSite.wednesdayEndTime}`) : null}
												/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Thursday End Time">
												<TimePicker
													onChange={date => {
														let sites = candidateSites.slice();
														sites[candidateSite.siteId - 1].thursdayEndTime = date
															? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
															: "";
														setCandidateSites(sites);
													}}
													nullable
													format="24"
													value={candidateSite.thursdayEndTime && /^\d{2}:\d{2}$/.test(candidateSite.thursdayEndTime) ? new Date(`1970-01-01T${candidateSite.thursdayEndTime}`) : null}
												/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Friday End Time">
												<TimePicker
													onChange={date => {
														let sites = candidateSites.slice();
														sites[candidateSite.siteId - 1].fridayEndTime = date
															? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
															: "";
														setCandidateSites(sites);
													}}
													nullable
													format="24"
													value={candidateSite.fridayEndTime && /^\d{2}:\d{2}$/.test(candidateSite.fridayEndTime) ? new Date(`1970-01-01T${candidateSite.fridayEndTime}`) : null}
												/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Saturday End Time">
												<TimePicker
													onChange={date => {
														let sites = candidateSites.slice();
														sites[candidateSite.siteId - 1].saturdayEndTime = date
															? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
															: "";
														setCandidateSites(sites);
													}}
													nullable
													format="24"
													value={candidateSite.saturdayEndTime && /^\d{2}:\d{2}$/.test(candidateSite.saturdayEndTime) ? new Date(`1970-01-01T${candidateSite.saturdayEndTime}`) : null}
												/>
										</FormControl>
									</div>
									<div>
										<FormControl label="Sunday End Time">
												<TimePicker
													onChange={date => {
														let sites = candidateSites.slice();
														sites[candidateSite.siteId - 1].sundayEndTime = date
															? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
															: "";
														setCandidateSites(sites);
													}}
													nullable
													format="24"
													value={candidateSite.sundayEndTime && /^\d{2}:\d{2}$/.test(candidateSite.sundayEndTime) ? new Date(`1970-01-01T${candidateSite.sundayEndTime}`) : null}
												/>
										</FormControl>
									</div>
								</SiteDaysContainer>
							</FormControl>
						</div> 
					</>
				);
			})}
			<Button
				kind="secondary"
				onClick={() => {
					addCandidateSiteFields(candidateSites.length);
					setCandidateSites([
						...candidateSites,
						{
							jobCodeRate: hourlyRate,
							siteId: candidateSites.length + 1,
							customHourlyRate: false,
						} as CandidateSite,
					]);
				}}
			>
				Add another site
			</Button>
			<hr />

			<FormControl label="Days of work per week">
				<Input
					type="number"
					min={0}
					max={7}
					value={daysOfWorkPerWeek}
					onChange={(e) => {
						setDaysOfWorkPerWeek(parseInt(e.currentTarget.value));
					}}
				/>
			</FormControl>

			<FormControl label="Required in excess of 10 hours per day?">
				<Checkbox
					checked={requiredInExcessOf10HoursPerDay}
					onChange={() => {
						setRequiredInExcessOf10HoursPerDay(
							!requiredInExcessOf10HoursPerDay
						);
					}}
				/>
			</FormControl>

			<FormControl
				label={<label>Start date</label>}
				caption="If start date is known, subject to clean MOJ Report and acceptance"
			>
				<div>
					<StatefulDatepicker
						onChange={({ date }) =>
							setStartDate(Array.isArray(date) ? date : [date])
						}
						value={startDate}
						formatString="d MMMM yyyy"
						placeholder=" "
						mask={null}
					/>
				</div>
			</FormControl>

			<FormControl
				label={<label>Fixed Term End Date</label>}
				caption="Fixed term end date is only for fixed term employment agreement"
			>
				<div>
					<StatefulDatepicker
						onChange={({ date }) =>
							setFixedTermEndDate(Array.isArray(date) ? date : [date])
						}
						value={fixedTermEndDate}
						formatString="d MMMM yyyy"
						placeholder=" "
						mask={null}
					/>
				</div>
			</FormControl>

			<FormControl
				label={
					<label
						className={requiredFields.includes("managerName") ? "required" : ""}
					>
						Supervisor / Manager Name
					</label>
				}
				error={fieldErrors.managerName}
			>
				<Input
					value={managerName}
					onChange={(e) => {
						setManagerName(e.currentTarget.value);
					}}
				/>
			</FormControl>
			<FormControl
				label={
					<label
						className={
							requiredFields.includes("managerContactNumber") ? "required" : ""
						}
					>
						Supervisor / Manager Contact Number
					</label>
				}
				error={fieldErrors.managerContactNumber}
			>
				<Input
					value={managerContactNumber}
					onChange={(e) => {
						setManagerContactNumber(e.currentTarget.value);
					}}
				/>
			</FormControl>

			<hr />
			<FormControl label={"Which onboarding tasks should be allocated?"}>
				<>
					{allOnboardingTaskSets && allOnboardingTaskSets.items && (
						<GridSpacer>
							{allOnboardingTaskSets.items.map((item: any, i: number) => {
								return (
									<div>
										<Checkbox
											checked={selectedTaskSets.includes(item.identifier)}
											onChange={() => {
												let tmpSelectedTaskSets = [...selectedTaskSets];

												if (tmpSelectedTaskSets.includes(item.identifier)) {
													tmpSelectedTaskSets.splice(
														tmpSelectedTaskSets.indexOf(item.identifier),
														1
													);
												} else {
													tmpSelectedTaskSets.push(item.identifier);
												}
												setSelectedTaskSets(tmpSelectedTaskSets);
											}}
										>
											{item.label}
										</Checkbox>
									</div>
								);
							})}
						</GridSpacer>
					)}
				</>
			</FormControl>

			<hr />
			<FormControl
				label={
					<label
						className={requiredFields.includes("allocatedTo") ? "required" : ""}
					>
						Allocate To
					</label>
				}
				error={fieldErrors.allocatedTo}
			>
				<>
					{adminUsers && (
						<StyledSelect
							labelKey={"name"}
							valueKey={"id"}
							options={adminUsers}
							maxDropdownHeight="12rem"
							clearable={false}
							value={allocatedTo}
							onChange={(params) => {
								setAllocatedTo(params.value);
							}}
						/>
					)}
				</>
			</FormControl>

			<hr />
			<FormControl
				label={
					<label
						className={
							requiredFields.includes("ppcsConfirmed") ? "required" : ""
						}
					>
						Do you confirm that all details loaded into this form are correct?
					</label>
				}
				error={fieldErrors.ppcsConfirmed}
			>
				<Checkbox
					checked={ppcsConfirmed}
					onChange={() => {
						setPpcsConfirmed(!ppcsConfirmed);
					}}
				/>
			</FormControl>
			<br />
			<br />
			<Button onClick={validateAndSave} isLoading={isSaving}>
				Create Candidate
			</Button>
			{/* {candidateCreated && (
				<Modal
					closeable={false}
					isOpen={candidateCreated}
					animate
					autoFocus
					role="dialog"
				>
					<ModalHeader>
						{firstName} {lastName} Created
					</ModalHeader>
					<ModalBody>
						A temporary password has been set. This will last for seven days.
						When you close this window, these details will be lost
						<br />
						<br />
						Username: <Input value={contactEmail} /> <br />
						Password:{" "}
							<Input type="password" value={candidate.temporaryPassphrase} />					
						<br />
						<br />
							<a href={candidate.onboardingPackUrl} target="_blank">
							Download Onboarding Pack for Printing
							</a>
					</ModalBody>
					<ModalFooter>
						<ModalButton onClick={allDone}>Okay</ModalButton>
					</ModalFooter>
				</Modal>
			)} */}
		</Container>
	);
};
export default CandidateCreate;
